import React, { useState, useEffect, useRef } from 'react';
import { QuestType, QuestDifficulty, SubTask } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

interface CreateQuestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (
    title: string, 
    description: string, 
    type: QuestType, 
    difficulty: QuestDifficulty, 
    resetDaily: boolean,
    scheduledFor?: string,
    subTasks?: SubTask[],
    scheduledTime?: { start: string; end: string },
    allDay?: boolean
  ) => void;
  prefilledDate?: string;
  prefilledTime?: string;
}

const CreateQuestModal: React.FC<CreateQuestModalProps> = ({ isOpen, onClose, onCreate, prefilledDate, prefilledTime }) => {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<QuestType>('main');
  const [difficulty, setDifficulty] = useState<QuestDifficulty>('normal');
  const [resetDaily, setResetDaily] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(prefilledDate || '');
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const [newSubTaskText, setNewSubTaskText] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState(prefilledTime || '09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showDifficultyDropdown, setShowDifficultyDropdown] = useState(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const difficultyDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
      if (difficultyDropdownRef.current && !difficultyDropdownRef.current.contains(event.target as Node)) {
        setShowDifficultyDropdown(false);
      }
    };

    if (showTypeDropdown || showDifficultyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypeDropdown, showDifficultyDropdown]);

  // If opened from tap-create with a prefilled time, default end time to +1 hour
  useEffect(() => {
    if (isOpen && prefilledTime) {
      const [h, m] = prefilledTime.split(':').map(Number);
      const endMinutes = h * 60 + m + 60;
      const eh = String(Math.floor(endMinutes / 60)).padStart(2, '0');
      const em = String(endMinutes % 60).padStart(2, '0');
      setEndTime(`${eh}:${em}`);
    }
  }, [isOpen, prefilledTime]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // Prepare scheduledTime
    const scheduledTime = (!allDay && scheduledFor && startTime && endTime) 
      ? { start: startTime, end: endTime }
      : undefined;

    onCreate(
      title, 
      description, 
      type, 
      difficulty, 
      resetDaily,
      scheduledFor || getTodayDateString(),
      subTasks.length > 0 ? subTasks : undefined,
      scheduledTime,
      allDay
    );
    
    // Reset form
    setTitle('');
    setDescription('');
    setType('main');
    setDifficulty('normal');
    setResetDaily(false);
    setScheduledFor('');
    setSubTasks([]);
    setNewSubTaskText('');
    setAllDay(false);
    setStartTime('09:00');
    setEndTime('10:00');
    setShowTypeDropdown(false);
    setShowDifficultyDropdown(false);
    onClose();
  };

  const addSubTask = () => {
    if (newSubTaskText.trim()) {
      setSubTasks([
        ...subTasks,
        {
          id: Date.now().toString(),
          text: newSubTaskText.trim(),
          completed: false,
        }
      ]);
      setNewSubTaskText('');
    }
  };

  const removeSubTask = (id: string) => {
    setSubTasks(subTasks.filter(st => st.id !== id));
  };

  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const questTypeOptions = [
    { value: 'daily', label: t.quests.daily, iconPath: '/icons/icon-quest-daily.png' },
    { value: 'main', label: t.quests.main, iconPath: '/icons/icon-quest-main.png' },
    { value: 'instant', label: 'Instant', iconPath: '/icons/icon-quest-instant.png' },
    { value: 'boss', label: t.quests.boss, iconPath: '/icons/icon-quest-boss.png' },
  ];

  const difficultyOptions = [
    { value: 'easy', label: 'Easy', rankLabel: 'E-RANK', iconPath: '/achievements/ranks/rank_e_hunter.png' },
    { value: 'normal', label: 'Normal', rankLabel: 'C-RANK', iconPath: '/achievements/ranks/rank_c_hunter.png' },
    { value: 'hard', label: 'Hard', rankLabel: 'A-RANK', iconPath: '/achievements/ranks/rank_a_hunter.png' },
    { value: 'boss', label: 'Boss', rankLabel: 'S-RANK', iconPath: '/achievements/ranks/rank_s_hunter.png' },
  ];

  const selectedQuestType = questTypeOptions.find(opt => opt.value === type);
  const selectedDifficulty = difficultyOptions.find(opt => opt.value === difficulty);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-[100] p-3 sm:p-4 modal-backdrop animate-fade-in">
      <div className="relative max-w-md sm:max-w-2xl w-full animate-scale-in">
        {/* Main Modal */}
        <div className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl overflow-hidden"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-3 sm:p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
              <div>
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse"></div>
                  <span className="text-xs sm:text-sm font-orbitron text-system-blue tracking-wider uppercase">
                    ［ System ］
                  </span>
                </div>
                <h2 className="text-base sm:text-2xl font-bold font-orbitron text-white tracking-wider">
                  {t.dashboard.createQuest.toUpperCase()}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white text-lg sm:text-xl leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-1.5 sm:p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
                style={{
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                }}
              >
                ✕
              </button>
          </div>

          {/* Scrollable Content */}
          <div className="p-3 sm:p-6 max-h-[calc(75vh-64px)] sm:max-h-[calc(85vh-96px)] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* Title Input */}
              <div>
                <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-2">
                  {t.quests.title} *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-2xl px-4 py-3 text-sm sm:text-base text-white font-rajdhani placeholder:text-white/80 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                  placeholder={t.quests.titlePlaceholder}
                  required
                />
              </div>

              {/* Description Input */}
              <div>
                <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-2">
                  {t.quests.description}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-2xl px-4 py-3 text-sm sm:text-base text-white font-rajdhani placeholder:text-white/80 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all resize-none"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                  placeholder={t.quests.descriptionPlaceholder}
                  rows={3}
                />
              </div>

              {/* Type and Difficulty */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-2">
                    {t.quests.questType}
                  </label>
                  {/* Custom Dropdown */}
                  <div className="relative" ref={typeDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                      className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer flex items-center justify-between hover:border-blue-500/50 hover:scale-105"
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <img 
                          src={selectedQuestType?.iconPath} 
                          alt={selectedQuestType?.label}
                          className="w-5 h-5 object-contain"
                          style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                        />
                        <span>{selectedQuestType?.label}</span>
                      </div>
                      <span className="text-system-blue">▼</span>
                    </button>
                    
                    {showTypeDropdown && (
                      <div className="absolute z-50 w-full mt-2 bg-gradient-to-b from-slate-900/95 via-slate-950/95 to-slate-950/95 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-700/70 border-b-slate-700/70 rounded-2xl"
                        style={{
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8), 0 2px 8px rgba(0, 0, 0, 0.6)'
                        }}
                      >
                        {questTypeOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setType(option.value as QuestType);
                              setShowTypeDropdown(false);
                            }}
                            className={`w-full px-4 py-3 text-left font-rajdhani transition-colors flex items-center gap-2 ${
                              type === option.value 
                                ? 'bg-system-blue/20 text-system-blue' 
                                : 'text-white hover:bg-system-blue/10'
                            }`}
                          >
                            <img 
                              src={option.iconPath} 
                              alt={option.label}
                              className="w-5 h-5 object-contain"
                              style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                            />
                            <span>{option.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-2">
                    {t.quests.difficulty}
                  </label>
                  {/* Custom Difficulty Dropdown */}
                  <div className="relative" ref={difficultyDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setShowDifficultyDropdown(!showDifficultyDropdown)}
                        className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer flex items-center justify-between hover:border-blue-500/50 hover:scale-105"
                        style={{
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      >
                      <div className="flex items-center gap-2">
                        <img 
                          src={selectedDifficulty?.iconPath} 
                          alt={selectedDifficulty?.label}
                          className="w-6 h-6 object-contain"
                          style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' }}
                        />
                        <span className="text-sm">
                          <span className="text-system-blue font-bold">{selectedDifficulty?.rankLabel}</span>
                          <span className="text-white"> ({selectedDifficulty?.label})</span>
                        </span>
                      </div>
                      <span className="text-system-blue">▼</span>
                    </button>
                    
                    {showDifficultyDropdown && (
                      <div className="absolute z-50 w-full mt-2 bg-gradient-to-b from-slate-900/95 via-slate-950/95 to-slate-950/95 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-700/70 border-b-slate-700/70 rounded-2xl"
                        style={{
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.8), 0 2px 8px rgba(0, 0, 0, 0.6)'
                        }}
                      >
                        {difficultyOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setDifficulty(option.value as QuestDifficulty);
                              setShowDifficultyDropdown(false);
                            }}
                            className={`w-full px-4 py-3 text-left font-rajdhani transition-colors flex items-center gap-2 ${
                              difficulty === option.value 
                                ? 'bg-system-blue/20 text-system-blue' 
                                : 'text-white hover:bg-system-blue/10'
                            }`}
                          >
                            <img 
                              src={option.iconPath} 
                              alt={option.label}
                              className="w-6 h-6 object-contain"
                              style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.8))' }}
                            />
                            <span className="text-sm">
                              <span className="font-bold">{option.rankLabel}</span>
                              <span> ({option.label})</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Scheduled Date & Time */}
              <div className="bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl p-3 sm:p-6"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <img 
                    src="/icons/icon-quest-scheduled.png" 
                    alt="Schedule"
                    className="w-5 h-5 object-contain"
                    style={{ filter: 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.6))' }}
                  />
                  <label className="text-sm font-rajdhani text-gray-400 uppercase tracking-wide">
                    {t.quests.schedule}
                  </label>
                </div>

                {/* Date Picker */}
                <div className="mb-3">
                  <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    min={getTodayDateString()}
                    className="w-full bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer"
                    style={{ 
                      colorScheme: 'dark',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                </div>

                {/* All Day Toggle */}
                {scheduledFor && (
                  <div className="mb-3">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={allDay}
                          onChange={(e) => setAllDay(e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-12 h-6 rounded-full transition-all ${allDay ? 'bg-purple-600' : 'bg-gray-600'}`}>
                          <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${allDay ? 'translate-x-6' : ''}`} />
                        </div>
                      </div>
                      <span className="text-sm font-rajdhani text-gray-300 group-hover:text-white transition-colors">
                        All Day Quest
                      </span>
                    </label>
                  </div>
                )}

                {/* Time Pickers */}
                {scheduledFor && !allDay && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-1">
                        ⏰ Start Time
                      </label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-full bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer"
                        style={{ 
                          colorScheme: 'dark',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-1">
                        ⏰ End Time
                      </label>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="w-full bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-2xl px-4 py-3 text-white font-rajdhani focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all cursor-pointer"
                        style={{ 
                          colorScheme: 'dark',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                        }}
                      />
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 font-rajdhani mt-3">
                  {t.quests.scheduleDesc}
                </p>
              </div>

              {/* Sub-tasks Section */}
              <div className="bg-gradient-to-b from-green-950/40 via-slate-950/30 to-green-950/40 backdrop-blur-sm border-t-2 border-l-2 border-green-500/40 border-r-2 border-b-2 border-r-green-700/60 border-b-green-700/60 rounded-3xl p-3 sm:p-6"
                style={{
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">✓</span>
                  <label className="text-sm font-rajdhani text-gray-400 uppercase tracking-wide">
                    {t.quests.subTasks}
                  </label>
                </div>
                
                {/* Add Sub-task Input */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newSubTaskText}
                    onChange={(e) => setNewSubTaskText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubTask())}
                    className="flex-1 bg-white/10 backdrop-blur-sm border-t-2 border-l-2 border-slate-400/40 border-r-2 border-b-2 border-r-slate-600/60 border-b-slate-600/60 rounded-xl px-3 py-2 text-white text-sm font-rajdhani placeholder:text-white/80 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/30 transition-all"
                    placeholder={t.quests.addSubTask}
                    style={{
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                  <button
                    type="button"
                    onClick={addSubTask}
                    className="px-4 py-2 bg-gradient-to-b from-green-600/90 to-green-700/90 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 rounded-xl font-orbitron font-bold text-sm transition-all hover:scale-105 relative"
                    style={{
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                    <span className="relative z-10">+ {t.common.add}</span>
                  </button>
                </div>

                {/* Sub-tasks List */}
                {subTasks.length > 0 && (
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                    {subTasks.map((subTask) => (
                      <div
                        key={subTask.id}
                        className="flex items-center justify-between bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/30 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl px-3 py-2 group"
                        style={{
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-4 h-4 border-2 border-green-500/50 rounded"></div>
                          <span className="text-sm text-gray-300 font-rajdhani">{subTask.text}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSubTask(subTask.id)}
                          className="text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reset Daily Toggle */}
              <div className="flex items-center justify-between bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-4 py-3"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <label htmlFor="resetDaily" className="text-sm font-rajdhani text-gray-300 cursor-pointer flex items-center gap-2 select-none">
                  <span>🔄</span>
                  <span>Reset this quest daily</span>
                </label>
                <button
                  type="button"
                  aria-pressed={resetDaily}
                  onClick={() => setResetDaily(!resetDaily)}
                  className={`relative w-14 h-6 sm:w-12 sm:h-6 rounded-full transition-all duration-200 ease-out border border-white/10 ${
                    resetDaily
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-[0_0_0_2px_rgba(59,130,246,0.2)_inset]'
                      : 'bg-gradient-to-b from-slate-600 to-slate-700'
                  } focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/50`}
                >
                  <div
                    className={`absolute left-1 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full transition-transform duration-200 ease-out shadow-[0_1px_2px_rgba(0,0,0,0.25)] ${
                      resetDaily ? 'translate-x-8 sm:translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Reward Preview */}
              <div className="bg-gradient-to-b from-amber-950/40 via-yellow-950/30 to-amber-950/40 backdrop-blur-sm border-t-2 border-l-2 border-amber-500/40 border-r-2 border-b-2 border-r-amber-700/60 border-b-amber-700/60 rounded-3xl p-3 sm:p-6"
                style={{
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 text-xs text-system-purple font-orbitron uppercase tracking-wider mb-2">
                  <img 
                    src="/icons/icon-quest-instant.png" 
                    alt="Rewards"
                    className="w-4 h-4 object-contain"
                    style={{ filter: 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.6))' }}
                  />
                  {t.quests.rewards}
                </div>
                <div className="flex gap-4 text-sm font-rajdhani">
                  <div className="flex items-center gap-1">
                    <img 
                      src="/icons/icon-xp.png" 
                      alt="XP"
                      className="w-4 h-4 object-contain"
                      style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.8))' }}
                    />
                    <span className="text-system-blue font-bold">
                      +{difficulty === 'easy' ? 10 : difficulty === 'normal' ? 20 : difficulty === 'hard' ? 40 : 100}
                    </span>
                    <span className="text-xs text-gray-400">XP</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <img 
                      src="/icons/icon-gold.png" 
                      alt="Gold"
                      className="w-4 h-4 object-contain"
                      style={{ filter: 'drop-shadow(0 0 4px rgba(252, 211, 77, 0.8))' }}
                    />
                    <span className="font-bold font-orbitron" style={{ color: '#FCD34D' }}>
                      +{difficulty === 'easy' ? 1 : difficulty === 'normal' ? 2 : difficulty === 'hard' ? 4 : 10}
                    </span>
                    <span className="text-xs text-gray-400">Gold</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <img 
                      src="/icons/icon-statpoints.png" 
                      alt="Stats"
                      className="w-4 h-4 object-contain"
                      style={{ filter: 'drop-shadow(0 0 4px rgba(168, 85, 247, 0.6))' }}
                    />
                    <span className="text-system-purple font-bold">
                      +{difficulty === 'easy' ? 1 : difficulty === 'normal' ? 2 : difficulty === 'hard' ? 4 : 6}
                    </span>
                    <span className="text-xs text-gray-400">Stats</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 relative group bg-gradient-to-b from-blue-800/60 via-purple-800/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 rounded-2xl px-4 sm:px-6 py-3 sm:py-4 font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 overflow-hidden text-white"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  <span className="relative z-10">{t.dashboard.createQuest.toUpperCase()}</span>
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 relative"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-2xl"></div>
                  <span className="relative z-10">{t.common.cancel.toUpperCase()}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateQuestModal;
